<script lang="ts" setup>
const { getCompanyAttributes } = useApiAttributes()
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const profiles: UTabItem[] = [
  {
    label: t('profiles.full'),
    value: 'full',
  },
  {
    label: t('profiles.simplified'),
    value: 'simplified',
  },
]

const companyAttributes = computed((): ApiAttribute[] => {
  return getCompanyAttributes({
    simplified: profile.value === 1,
    version: 'v2',
  })
})

const profile = computed({
  get: () => {
    return profiles.findIndex((profile) => {
      return profile.value === (route.query.profile || 'full')
    })
  },
  set: (index: number) => {
    const tab = profiles[index]

    router.replace(localePath({
      name: route.name,
      query: { profile: tab.value },
    }))
  },
})

const response = computed(() => {
  const response: Record<string, any> = {}

  const processAttributes = (parentObject: Record<string, any>, attributes: any[]) => {
    for (const attribute of attributes) {
      if (attribute.attributes?.length) {
        if (attribute.exampleToOverride) {
          parentObject[attribute.name] = attribute.exampleToOverride
        }
        else {
          parentObject[attribute.name] = attribute.type === 'array' ? [] : {}

          processAttributes(parentObject[attribute.name], attribute.attributes)
        }
      }
      else {
        if (Array.isArray(parentObject)) {
          if (parentObject.length === 0) {
            parentObject.push({})
          }

          parentObject[parentObject.length - 1][attribute.name] = attribute.example
        }
        else {
          parentObject[attribute.name] = attribute.example
        }
      }
    }
  }

  for (const section of companyAttributes.value) {
    response[section.name] = {}
    processAttributes(response[section.name], section.attributes || [])
  }

  return response
})
</script>

<template>
  <div class="flex flex-col items-center gap-6">
    <UTabs
      v-model="profile"
      :items="profiles"
    />

    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <ApiAttributes
        :attributes="companyAttributes"
        class="max-h-96 min-h-96 md:sticky md:max-h-[calc(100vh-14rem-var(--header-height))]"
      />

      <ApiResponse
        class="max-h-96 min-h-96 md:sticky md:max-h-[calc(100vh-14rem-var(--header-height))]"
        :level-to-open="1"
        :response="response"
      />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "profiles": {
      "full": "Full Profile",
      "simplified": "Simplified"
    }
  },
  "es": {
    "profiles": {
      "full": "Perfil completo",
      "simplified": "Simplificado"
    }
  },
  "fr": {
    "profiles": {
      "full": "Profil complet",
      "simplified": "Simplifié"
    }
  }
}
</i18n>
